import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useId, useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import useValueToLabelCache from '@/hooks/useValueToLabelCache';
import type { ApiResponseBody } from '@/lib/api/api-type-helper';
import { apiClient } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import IconButton from '../Button/IconButton';
import Combobox from '../Combobox';
import Icon from '../Icon/Icon';
import { Button } from '../ui/button';

// 代表して BusinessPlaceTypeSelect のレスポンス型を参照。
export type PlaceOption = ApiResponseBody<'/v2/components/place-select', 'get'>['items'][number];
type Props = {
  value?: string;
  /** 初期表示用のラベル。可能であれば指定したほうが表示がスムーズです。 */
  displayValue?: string;
  onChange: (option?: PlaceOption) => void;
  allowEmpty?: boolean;
  hideSearch?: boolean;
  disabled?: boolean;
  placeHolder?: string;
  width?: number;
  className?: string;
  /** 指定しない場合自律制御 */
  open?: boolean;
  setOpen?: (open: boolean) => void;
};

/**
 * Place のセレクト
 */
export function PlaceSelect(props: Props) {
  const tc = useCommonTranslation();
  const [_open, _setOpen] = useState(false);
  const open = props.open ?? _open;
  const setOpen = props.setOpen ?? _setOpen;
  const popoverContentId = useId();
  const [searchText, setSearchText] = useState('');
  // 1文字ごとの再取得を抑える。debounce した値だけを queryKey に使う。
  const debouncedSearchText = useDebouncedValue(searchText);
  const valueToLabelCache = useValueToLabelCache();
  const {
    data,
    isPending,
    isFetching,
    isPlaceholderData
  } = useQuery({
    queryKey: ['place-select', debouncedSearchText],
    queryFn: async ({
      queryKey: [_, freeText]
    }) => await apiClient.GET('/v2/components/place-select', {
      params: {
        query: {
          pageSize: 20,
          freeText
        }
      }
    }),
    select: data => data.data?.items.map(item => ({
      value: item.id,
      label: item.name,
      avatarUrl: item.avatarUrl,
      data: item
    })),
    // 再取得中も直前の候補を保持し、検索のたびに一覧が空へ落ちてチラつくのを防ぐ。
    placeholderData: keepPreviousData
  });
  const isLoading = data == null;
  const label = useMemo(() => {
    const itemLabel = data?.find(option => option.data.id === props.value)?.label;
    const cache = valueToLabelCache.get(props.value ?? '');
    return itemLabel || cache || props.displayValue;
  }, [data, props.displayValue, props.value, valueToLabelCache]);
  const canClear = Boolean(props.allowEmpty && !props.disabled && props.value);
  return <Popover open={open} onOpenChange={() => {
    setOpen(!open);
    if (!open) setSearchText('');
  }}>
      <div className={cn('relative inline-block', props.className)} style={props.width ? {
      width: props.width
    } : undefined}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={open} aria-controls={popoverContentId} className={cn('relative w-full justify-start overflow-hidden text-ellipsis pr-10', !label && 'text-muted-foreground')} disabled={props.disabled}>
            <span className={cn('min-w-0 truncate', canClear && 'pr-8')}>
              {label || props.placeHolder || (isLoading ? '' : tc('pleaseSelect'))}
            </span>
            <div className="absolute inset-y-0 right-3 flex items-center">
              <Icon iconName="ChevronDownIcon" className="shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>

        {canClear && <div className="absolute inset-y-0 right-8 flex items-center">
            <IconButton icon="XIcon" variant="mutedGhost" size="ExtraSmall" aria-label="Clear selection" onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          props.onChange(undefined);
        }} onPointerDown={e => {
          e.preventDefault();
          e.stopPropagation();
        }} />
          </div>}
      </div>
      <PopoverContent id={popoverContentId} className="w-full overflow-hidden p-0" style={props.width ? {
      width: props.width
    } : undefined} align="start">
        <Combobox type="single" value={props.value ?? null} selectionDisplayStyle="hidden" onValueChange={value => {
        const option = data?.find(option => option.data.id === value);
        if (option == null) {
          return; // 値クリアの挙動はここでは行わない
        }
        props.onChange(option.data);
        setSearchText('');
        setOpen(false);
      }} isPending={isPending} isFetching={isFetching} isPlaceholderData={isPlaceholderData} options={data ?? []} onSearchChange={setSearchText} valueToLabelCache={valueToLabelCache} hideSearch={props.hideSearch} />
      </PopoverContent>
    </Popover>;
}